/**
 * The Build board's view state lives in the URL and nowhere else
 * (CR-BUILD-BOARD-EPICS-2026-09-26, INV-R.4 / spec R.6.3): `?view=tasks`
 * selects the Tasks view, `&epic=<legacyId|none>` narrows it. No Redux field
 * and no component state mirrors either parameter.
 */

import { UNASSIGNED_EPIC_KEY, type BoardView, type EpicFilter } from './types'

export interface ParsedBoardView {
  view: BoardView
  epicFilter: EpicFilter
}

const ALL: EpicFilter = { kind: 'all' }

/**
 * Total parse: an absent or unknown `view` is the Epics view, and `epic` is
 * read only under `view=tasks` (an empty `epic` means no filter).
 */
export function parseBoardView(params: URLSearchParams): ParsedBoardView {
  if (params.get('view') !== 'tasks') return { view: 'epics', epicFilter: ALL }
  const epic = params.get('epic')
  if (epic === null || epic === '') return { view: 'tasks', epicFilter: ALL }
  return {
    view: 'tasks',
    epicFilter: epic === UNASSIGNED_EPIC_KEY ? { kind: 'unassigned' } : { kind: 'epic', id: epic }
  }
}

/**
 * The `search` string for a view write, preserving every foreign query key.
 * The Epics view is the canonical form without `view`; toggling to it drops
 * `epic` too. Returns `''` when no key remains.
 */
export function boardViewSearch(
  current: URLSearchParams,
  next: { view: BoardView; epicFilter?: EpicFilter }
): string {
  const params = new URLSearchParams(current)
  params.delete('view')
  params.delete('epic')
  if (next.view === 'tasks') {
    params.set('view', 'tasks')
    const filter = next.epicFilter ?? ALL
    if (filter.kind === 'epic') params.set('epic', filter.id)
    if (filter.kind === 'unassigned') params.set('epic', UNASSIGNED_EPIC_KEY)
  }
  const text = params.toString()
  return text === '' ? '' : `?${text}`
}
